import React, { useState, useEffect } from 'react';
import Navbar from './Navbar';
import Calling from '../../../api/Calling';
import { toast } from 'react-toastify';
import { FiSave, FiRefreshCw } from 'react-icons/fi';
import { SiGoogleads, SiGoogleadsense } from 'react-icons/si';
import { FaBuysellads } from 'react-icons/fa';
import { PiCoinsFill } from "react-icons/pi";
import generateCaptcha from '../../../components/captcha';

const ReelsSetting = () => {
    const [loader, setLoader] = useState(false);
    const [saveLoader, setSaveLoader] = useState(false);
    const [settings, setSettings] = useState({
        banner_pos_start: 0,
        banner_pos_end: 0,
        banner_skip_timing: 0,
        fs_pos_start: 0,
        fs_pos_end: 0,
        ntv_pos_start: 0,
        ntv_pos_end: 0,
        ntv_skip_timing: 0,
        reels_coin_start: 0,
        reels_coin_end: 0,
        reels_coin_allow_start: 1,
        reels_coin_allow_end: 1
    });

    // Fetch settings
    const fetchSettings = async () => {
        try {
            setLoader(true);
            const resp = await Calling.getReelsSetting();
            if (resp?.status) {
                setSettings({
                    banner_pos_start: resp?.data?.banner_pos_start || 0,
                    banner_pos_end: resp?.data?.banner_pos_end || 0,
                    banner_skip_timing: resp?.data?.banner_skip_timing || 0,
                    fs_pos_start: resp?.data?.fs_pos_start || 0,
                    fs_pos_end: resp?.data?.fs_pos_end || 0,
                    ntv_pos_start: resp?.data?.ntv_pos_start || 0,
                    ntv_pos_end: resp?.data?.ntv_pos_end || 0,
                    ntv_skip_timing: resp?.data?.ntv_skip_timing || 0,
                    reels_coin_start: resp?.data?.reels_coin_start || 0,
                    reels_coin_end: resp?.data?.reels_coin_end || 0,
                    reels_coin_allow_start: resp?.data?.reels_coin_allow_start || 1,
                    reels_coin_allow_end: resp?.data?.reels_coin_allow_end || 1
                });
            } else {
                toast.error(resp?.message || 'Failed to fetch settings');
            }
        } catch (error) {
            console.log('Error:', error);
            toast.error('Something went wrong');
        } finally {
            setLoader(false);
        }
    };

    useEffect(() => {
        fetchSettings();
    }, []);

    // Handle input change
    const handleChange = (e) => {
        const { name, value } = e.target;
        setSettings(prev => ({
            ...prev,
            [name]: parseInt(value) || 0
        }));
    };

    // Save settings
    const handleSave = async () => {
        // Validation
        if (settings.banner_pos_start > settings.banner_pos_end) {
            return toast.error('Banner position start cannot be greater than end');
        }
        if (settings.fs_pos_start > settings.fs_pos_end) {
            return toast.error('Full Screen position start cannot be greater than end');
        }
        if (settings.ntv_pos_start > settings.ntv_pos_end) {
            return toast.error('Native position start cannot be greater than end');
        }
        if (settings.reels_coin_start > settings.reels_coin_end) {
            return toast.error('Reels coin start cannot be greater than end');
        }
        if (settings.reels_coin_allow_start < 1) {
            return toast.error('Coin allow start must be at least 1');
        }
        if (settings.reels_coin_allow_end < 1) {
            return toast.error('Coin allow end must be at least 1');
        }
        if (settings.reels_coin_allow_start > settings.reels_coin_allow_end) {
            return toast.error('Coin allow start cannot be greater than end');
        }
        const content = `You are about to change reels setting. To confirm, please solve this math problem.`;
        const check = generateCaptcha(content);
        if (!check) return toast.error("Captcha answer is incorrect", { autoClose: 2000 });
        try {
            setSaveLoader(true);
            const resp = await Calling.updateReelsSetting(settings);
            if (resp?.status) {
                toast.success('Settings updated successfully!');
            } else {
                toast.error(resp?.message || 'Failed to update settings');
            }
        } catch (error) {
            console.log('Error:', error);
            toast.error('Something went wrong');
        } finally {
            setSaveLoader(false);
        }
    };

    return (
        <div className="w-full text-main-font mt-16 px-3 md:ml-[4.4rem]">
            <h1 className="text-xl md:text-2xl font-bold mt-4 text-main-font w-auto">Reels Management</h1>
            <div className='bg-main-white rounded-xl shadow-lg'>
                <Navbar selectState={'setting'} />
                <hr className='border-main-border' />

                {loader ? (
                    <div className="flex items-center justify-center py-20">
                        <div className="flex flex-col items-center gap-3">
                            <div className="w-10 h-10 border-4 border-main-blue border-t-transparent rounded-full animate-spin"></div>
                            <span className="text-main-subfont">Loading settings...</span>
                        </div>
                    </div>
                ) : (
                    <div className='px-4 py-2'>
                        <div className="flex items-center justify-between mb-2">
                            <p className="text-main-subfont">Configure reels ads and coin settings</p>
                            <button
                                onClick={fetchSettings}
                                className="flex items-center gap-2 px-3 py-2 border border-main-border rounded-lg text-main-font hover:bg-main-bg transition-colors text-sm"
                            >
                                <FiRefreshCw className={loader ? 'animate-spin' : ''} />
                                Refresh
                            </button>
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            {/* Banner Ads Settings */}
                            <div className="border border-main-border rounded-xl p-4">
                                <h3 className="text-lg font-semibold text-main-font mb-4 pb-2 border-b border-main-border flex items-center gap-2">
                                    <SiGoogleads /> Banner Ads Settings
                                </h3>
                                <div className="space-y-4">
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-main-font mb-1.5">
                                                Position Start
                                            </label>
                                            <input
                                                type="number"
                                                name="banner_pos_start"
                                                value={settings.banner_pos_start}
                                                onChange={handleChange}
                                                min="0"
                                                className="w-full px-3 py-2.5 border border-main-border rounded-lg focus:outline-none focus:border-main-blue text-main-font"
                                                placeholder="Enter start position"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-main-font mb-1.5">
                                                Position End
                                            </label>
                                            <input
                                                type="number"
                                                name="banner_pos_end"
                                                value={settings.banner_pos_end}
                                                onChange={handleChange}
                                                min="0"
                                                className="w-full px-3 py-2.5 border border-main-border rounded-lg focus:outline-none focus:border-main-blue text-main-font"
                                                placeholder="Enter end position"
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-main-font mb-1.5">
                                            Skip Timing (seconds)
                                        </label>
                                        <input
                                            type="number"
                                            name="banner_skip_timing"
                                            value={settings.banner_skip_timing}
                                            onChange={handleChange}
                                            min="0"
                                            className="w-full px-3 py-2.5 border border-main-border rounded-lg focus:outline-none focus:border-main-blue text-main-font"
                                            placeholder="Enter skip timing"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Full Screen Ads Settings */}
                            <div className="border border-main-border rounded-xl p-4">
                                <h3 className="text-lg font-semibold text-main-font mb-4 pb-2 border-b border-main-border flex items-center gap-2">
                                    <SiGoogleadsense /> Full Screen Ads Settings
                                </h3>
                                <div className="space-y-4">
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-main-font mb-1.5">
                                                Position Start
                                            </label>
                                            <input
                                                type="number"
                                                name="fs_pos_start"
                                                value={settings.fs_pos_start}
                                                onChange={handleChange}
                                                min="0"
                                                className="w-full px-3 py-2.5 border border-main-border rounded-lg focus:outline-none focus:border-main-blue text-main-font"
                                                placeholder="Enter start position"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-main-font mb-1.5">
                                                Position End
                                            </label>
                                            <input
                                                type="number"
                                                name="fs_pos_end"
                                                value={settings.fs_pos_end}
                                                onChange={handleChange}
                                                min="0"
                                                className="w-full px-3 py-2.5 border border-main-border rounded-lg focus:outline-none focus:border-main-blue text-main-font"
                                                placeholder="Enter end position"
                                            />
                                        </div>
                                    </div>
                                    <p className="text-xs text-main-subfont">
                                        Full screen ads will appear between position {settings.fs_pos_start} and {settings.fs_pos_end}
                                    </p>
                                </div>
                            </div>

                            {/* Native Ads Settings */}
                            <div className="border border-main-border rounded-xl p-4">
                                <h3 className="text-lg font-semibold text-main-font mb-4 pb-2 border-b border-main-border flex items-center gap-2">
                                    <FaBuysellads /> Native Ads Settings
                                </h3>
                                <div className="space-y-4">
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-main-font mb-1.5">
                                                Position Start
                                            </label>
                                            <input
                                                type="number"
                                                name="ntv_pos_start"
                                                value={settings.ntv_pos_start}
                                                onChange={handleChange}
                                                min="0"
                                                className="w-full px-3 py-2.5 border border-main-border rounded-lg focus:outline-none focus:border-main-blue text-main-font"
                                                placeholder="Enter start position"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-main-font mb-1.5">
                                                Position End
                                            </label>
                                            <input
                                                type="number"
                                                name="ntv_pos_end"
                                                value={settings.ntv_pos_end}
                                                onChange={handleChange}
                                                min="0"
                                                className="w-full px-3 py-2.5 border border-main-border rounded-lg focus:outline-none focus:border-main-blue text-main-font"
                                                placeholder="Enter end position"
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-main-font mb-1.5">
                                            Skip Timing (seconds)
                                        </label>
                                        <input
                                            type="number"
                                            name="ntv_skip_timing"
                                            value={settings.ntv_skip_timing}
                                            onChange={handleChange}
                                            min="0"
                                            className="w-full px-3 py-2.5 border border-main-border rounded-lg focus:outline-none focus:border-main-blue text-main-font"
                                            placeholder="Enter skip timing"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Reels Coin Settings */}
                            <div className="border border-main-border rounded-xl p-4">
                                <h3 className="text-lg font-semibold text-main-font mb-4 pb-2 border-b border-main-border flex items-center gap-2">
                                    <PiCoinsFill /> Reels Coin Settings
                                </h3>
                                <div className="space-y-4">
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-main-font mb-1.5">
                                                Coin Start
                                            </label>
                                            <input
                                                type="number"
                                                name="reels_coin_start"
                                                value={settings.reels_coin_start}
                                                onChange={handleChange}
                                                min="0"
                                                className="w-full px-3 py-2.5 border border-main-border rounded-lg focus:outline-none focus:border-main-blue text-main-font"
                                                placeholder="Enter min coins"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-main-font mb-1.5">
                                                Coin End
                                            </label>
                                            <input
                                                type="number"
                                                name="reels_coin_end"
                                                value={settings.reels_coin_end}
                                                onChange={handleChange}
                                                min="0"
                                                className="w-full px-3 py-2.5 border border-main-border rounded-lg focus:outline-none focus:border-main-blue text-main-font"
                                                placeholder="Enter max coins"
                                            />
                                        </div>
                                    </div>
                                    <p className="text-xs text-main-subfont">
                                        Users will earn between {settings.reels_coin_start} - {settings.reels_coin_end} coins per reel
                                    </p>

                                    {/* Coin Allow Range - Random check */}
                                    <div className="pt-3 border-t border-main-border">
                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-sm font-medium text-main-font mb-1.5">
                                                    Allow Start
                                                </label>
                                                <input
                                                    type="number"
                                                    name="reels_coin_allow_start"
                                                    value={settings.reels_coin_allow_start}
                                                    onChange={handleChange}
                                                    min="1"
                                                    className="w-full px-3 py-2.5 border border-main-border rounded-lg focus:outline-none focus:border-main-blue text-main-font"
                                                    placeholder="Enter allow start"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-main-font mb-1.5">
                                                    Allow End
                                                </label>
                                                <input
                                                    type="number"
                                                    name="reels_coin_allow_end"
                                                    value={settings.reels_coin_allow_end}
                                                    onChange={handleChange}
                                                    min="1"
                                                    className="w-full px-3 py-2.5 border border-main-border rounded-lg focus:outline-none focus:border-main-blue text-main-font"
                                                    placeholder="Enter allow end"
                                                />
                                            </div>
                                        </div>
                                        <p className="text-xs text-main-subfont mt-2">
                                            Random number between {settings.reels_coin_allow_start} - {settings.reels_coin_allow_end}. If result equals {settings.reels_coin_allow_start}, coins are allowed.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Save Button */}
                        <div className="flex justify-end mt-6 pt-4 border-t border-main-border">
                            <button
                                onClick={handleSave}
                                disabled={saveLoader}
                                className="flex items-center gap-2 bg-main-blue text-main-white px-6 py-2.5 rounded-lg font-medium hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {saveLoader ? (
                                    <>
                                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                        Saving...
                                    </>
                                ) : (
                                    <>
                                        <FiSave />
                                        Save Settings
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ReelsSetting;